/**
 * LOJA (10.4Y): compra itens que ajudam na evolução com "Fragmentos" (moeda ganha
 * jogando — NÃO é dinheiro real). Preço escala por nº de compras. UI pura; o Game
 * executa a compra (spendCoins + efeito) e chama refresh().
 */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import type { GameProgress } from "../progression/GameProgress";

export interface ShopHandlers {
  onClose: () => void;
  onBuy: (id: string) => void;
}

/** Preço atual de um item dado o nº de compras (priceBase * priceScale^count). */
export function shopItemPrice(id: string, count: number): number {
  const it = GameConfig.shop.items.find((i) => i.id === id);
  if (!it) return Infinity;
  return Math.round(it.priceBase * Math.pow(it.priceScale, count));
}

export class ShopScreen {
  private readonly root: HTMLDivElement;
  private readonly coinsEl: HTMLElement;
  private readonly listEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;

  constructor(uiRoot: HTMLElement, private readonly h: ShopHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel shop-panel">
        <div class="shop-head">
          <div><h2 class="shop-title"></h2><div class="shop-sub"></div></div>
          <div class="shop-head-r">
            <div class="shop-coins">🪙 <b class="shop-coins-n">0</b></div>
            <button class="btn scr-x" data-a="close" aria-label="fechar">✕</button>
          </div>
        </div>
        <div class="shop-list"></div>
        <div class="menu-btns"><button class="btn primary" data-a="close"></button></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.titleEl = this.root.querySelector(".shop-title")!;
    this.coinsEl = this.root.querySelector(".shop-coins-n")!;
    this.listEl = this.root.querySelector(".shop-list")!;
    // 10.9EA — o rodape "Voltar" segue sendo o botao rotulado; o novo ✕ do topo fecha igual.
    this.closeBtn = this.root.querySelector('.menu-btns [data-a="close"]')!;
    this.root.querySelectorAll<HTMLButtonElement>('[data-a="close"]').forEach((b) => { b.onclick = () => this.h.onClose(); });

    // 10.9DI — seções RECOLHÍVEIS por categoria (toca no título abre/fecha)
    let lastCat = "";
    let body: HTMLElement = this.listEl;
    for (const it of GameConfig.shop.items) {
      const cat = (it as { cat?: string }).cat ?? "";
      if (cat !== lastCat) {
        lastCat = cat;
        const sec = document.createElement("div");
        sec.className = this.listEl.querySelector(".shop-sec") ? "shop-sec collapsed" : "shop-sec"; // 10.9DJ — abre só a 1ª
        sec.dataset.cat = cat;
        const head = document.createElement("button");
        head.type = "button"; head.className = "shop-cat"; head.dataset.cat = cat;
        head.innerHTML = `<span class="shop-cat-t"></span><span class="shop-cat-x">▾</span>`;
        head.setAttribute("aria-expanded", String(!sec.classList.contains("collapsed")));
        head.onclick = () => {
          sec.classList.toggle("collapsed");
          head.setAttribute("aria-expanded", String(!sec.classList.contains("collapsed")));
        };
        body = document.createElement("div"); body.className = "shop-cat-body";
        sec.appendChild(head); sec.appendChild(body);
        this.listEl.appendChild(sec);
      }
      const row = document.createElement("div");
      row.className = "shop-item";
      row.dataset.id = it.id;
      row.innerHTML = `
        <div class="shop-ic">${it.icon}</div>
        <div class="shop-info"><div class="shop-name"></div><div class="shop-desc"></div><div class="shop-count"></div></div>
        <button class="btn shop-buy"><span class="shop-price"></span></button>`;
      // 10.9EJ — CONFIRMACAO antes de gastar: um toque errado na lista (que rola) comprava na hora.
      row.querySelector<HTMLButtonElement>(".shop-buy")!.onclick = () => {
        const btn = row.querySelector<HTMLButtonElement>(".shop-buy")!;
        if (btn.classList.contains("dim") || btn.disabled) { this.h.onBuy(it.id); return; } // sem saldo: o jogo avisa
        const name = row.querySelector(".shop-name")?.textContent ?? "";
        const price = row.querySelector(".shop-price")?.textContent ?? "";
        this.confirmBuy(i18n.t("shop.confirmBuy", { name, price }), () => this.h.onBuy(it.id));
      };
      body.appendChild(row);
    }
    i18n.onChange(() => this.renderStatic());
    this.renderStatic();
  }

  private renderStatic(): void {
    this.titleEl.textContent = i18n.t("shop.title");
    const sub = this.root.querySelector(".shop-sub");
    if (sub) sub.textContent = i18n.t("shop.subtitle");
    this.closeBtn.textContent = i18n.t("menu.back");
    this.listEl.querySelectorAll<HTMLElement>(".shop-cat").forEach((h) => { h.querySelector(".shop-cat-t")!.textContent = i18n.t(`shop.cat.${h.dataset.cat}`); }); // 10.9DH/DI
    for (const it of GameConfig.shop.items) {
      const row = this.listEl.querySelector<HTMLElement>(`.shop-item[data-id="${it.id}"]`)!;
      row.querySelector(".shop-name")!.textContent = i18n.t(`shop.item.${it.id}.name`);
      row.querySelector(".shop-desc")!.textContent = i18n.t(`shop.item.${it.id}.desc`, { n: it.amount });
    }
  }

  open(p: GameProgress, focusId?: string): void {
    this.refresh(p);
    this.root.hidden = false;
    // 10.9L8 — abrir JÁ no item pedido (o aviso de perigo manda pro anel certo): abre a seção dele,
    // fecha as outras, rola até ele e dá um brilho pra achar de primeira
    this.listEl.querySelectorAll(".shop-item.focus").forEach((el) => el.classList.remove("focus"));
    const row = focusId ? this.listEl.querySelector<HTMLElement>(`.shop-item[data-id="${focusId}"]`) : null;
    if (!row) return;
    const sec = row.closest<HTMLElement>(".shop-sec");
    this.listEl.querySelectorAll<HTMLElement>(".shop-sec").forEach((s) => s.classList.toggle("collapsed", s !== sec));
    row.classList.add("focus");
    requestAnimationFrame(() => row.scrollIntoView({ block: "center" }));
  }
  hide(): void {
    this.root.hidden = true;
  }

  /** Atualiza saldo + preços + habilita/desabilita compra conforme o saldo. */
  refresh(p: GameProgress): void {
    this.coinsEl.textContent = String(p.coins);
    for (const it of GameConfig.shop.items) {
      const row = this.listEl.querySelector<HTMLElement>(`.shop-item[data-id="${it.id}"]`)!;
      const btn = row.querySelector<HTMLButtonElement>(".shop-buy")!;
      const priceEl = row.querySelector(".shop-price")!;
      // item único já adquirido (ex.: pet)
      if (it.id === "pet" && p.petOwned) {
        // 10.9L21 — mostra o nível do pet aqui (era só "Adquirido" pra sempre — não dava pra ver que ele evolui)
        priceEl.textContent = i18n.t("pet.shopLevel", { n: p.petLevel });
        btn.disabled = true;
        btn.classList.remove("locked");
        continue;
      }
      // 10.5T — mostra o quanto já foi comprado (o preço subia sem o jogador saber por quê)
      const count = p.shopCount(it.id);
      const countEl = row.querySelector<HTMLElement>(".shop-count")!;
      countEl.textContent = count > 0 ? i18n.t("shop.bought.count", { n: count }) : "";
      countEl.hidden = count === 0;
      const price = shopItemPrice(it.id, count);
      priceEl.textContent = `🪙 ${price}`;
      btn.disabled = p.coins < price;
      btn.classList.toggle("locked", p.coins < price);
    }
  }

  /** 10.9EJ — caixa de confirmacao (mesmo visual da do VIP). */
  private confirmBuy(text: string, onYes: () => void): void {
    this.root.querySelector(".vip-confirm")?.remove();
    const box = document.createElement("div");
    box.className = "vip-confirm";
    const esc = (t: string): string => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    box.innerHTML = `<div class="vip-confirm-box">
        <div class="vip-confirm-title">${esc(i18n.t("vip.confirmTitle"))}</div>
        <div class="vip-confirm-text">${esc(text)}</div>
        <div class="vip-confirm-btns">
          <button class="btn" data-a="no">${esc(i18n.t("vip.confirmNo"))}</button>
          <button class="btn primary" data-a="yes">${esc(i18n.t("vip.confirmYes"))}</button>
        </div>
      </div>`;
    box.addEventListener("click", (e) => { e.stopPropagation(); if (e.target === box) box.remove(); });
    box.querySelector('[data-a="no"]')!.addEventListener("click", () => box.remove());
    box.querySelector('[data-a="yes"]')!.addEventListener("click", () => { box.remove(); onYes(); });
    this.root.appendChild(box);
  }
}
